// Pure helpers for the Power Dial screen-pop lifecycle (kept out of the 600+ line run bar).

/**
 * Does a socket "call ended" event refer to the call the pop is showing?
 * An event without a callSid (session ended / cancelled) ends every pop.
 * @param {{callSid?: string}|null} pop
 * @param {{callSid?: string}|null|undefined} payload
 * @returns {boolean}
 */
export function popMatchesEnd(pop, payload) {
  if (!pop) return false;
  if (!payload?.callSid) return true;
  return String(payload.callSid) === String(pop.callSid);
}

/**
 * Next pop state after its call ended. Voicemail (AMD correction) closes it
 * outright; otherwise it closes unless the agent already started a
 * disposition or notes, in which case it stays open, marked ended.
 * @param {object|null} pop
 * @param {{callSid?: string}|null|undefined} payload
 * @param {{voicemail?: boolean}} [opts]
 * @returns {object|null}
 */
export function popAfterEnd(pop, payload, { voicemail = false } = {}) {
  if (!popMatchesEnd(pop, payload)) return pop;
  if (voicemail || !pop.dirty) return null;
  return { ...pop, ended: true };
}

/** Socket events that mean the answered call is over. */
export const POP_END_EVENTS = Object.freeze([
  'powerdial:agent_hangup',
  'phone:ended',
  'powerdial:session:completed',
  'powerdial:session:cancelled',
]);

/**
 * Should the local ringback tone be playing? Only while a running session has
 * a leg still dialing/ringing and no one has been connected.
 * @param {{status?: string, connectedCallSid?: string|null, currentSet?: Array<{status?: string}>}|null} session
 * @returns {boolean}
 */
export function shouldPlayRingTone(session) {
  if (!session || session.status !== 'running' || session.connectedCallSid) return false;
  const set = session.currentSet || [];
  if (set.some((m) => m.status === 'connected')) return false;
  return set.some((m) => m.status === 'dialing' || m.status === 'ringing');
}
